/**
 * AgentLicenses — manage one staff member's normalised licenses + phone
 * (Phase 10 step 16). Rendered inline under a roster row when "Manage" is
 * toggled.
 *
 * Reads:  GET   /admin/agents/{userId}/licenses
 * Writes: POST/PATCH/DELETE /admin/agents/{userId}/licenses[/{id}]
 *         PATCH /admin/agents/{userId}/phone
 *
 * Phone is the cross-tenant agent key (users.phone_e164) that onboarding + the
 * voice agent use to detect a dual-licensed agent — free-text in, E.164 out.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { licenseService } from "@re-platform/api-client";
import type {
  AgentLicense,
  LicenseType,
  StaffMember,
} from "@re-platform/api-client";
import { api } from "../../lib/api";

const LICENSE_TYPES: LicenseType[] = ["NMLS", "TREC", "DRE", "BRE"];

const field: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 13,
};

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

function PhoneEditor({
  member,
  onChanged,
}: {
  member: StaffMember;
  onChanged: () => void;
}) {
  const [phone, setPhone] = useState(member.phone_e164 ?? "");
  const mutation = useMutation({
    mutationFn: () => licenseService.setPhone(api, member.id, phone.trim()),
    onSuccess: (res) => {
      setPhone(res.phone_e164 ?? "");
      onChanged();
    },
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
        Phone (agent key)
      </span>
      <input
        value={phone}
        placeholder="(512) 555-0123"
        onChange={(e) => setPhone(e.target.value)}
        style={{ ...field, width: 180 }}
      />
      <button
        type="button"
        className="rp-btn rp-btn--secondary rp-btn--sm"
        disabled={mutation.isPending || phone.trim().length === 0}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? "Saving…" : "Save phone"}
      </button>
      {mutation.isError && (
        <span style={{ color: "var(--rp-color-danger)", fontSize: 12 }}>
          {errMsg(mutation.error, "Could not save phone")}
        </span>
      )}
    </div>
  );
}

function AddLicenseForm({
  userId,
  existingTypes,
  onAdded,
}: {
  userId: string;
  existingTypes: Set<string>;
  onAdded: () => void;
}) {
  const [type, setType] = useState<LicenseType>("TREC");
  const [number, setNumber] = useState("");
  const [state, setState] = useState("");
  const [expires, setExpires] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      licenseService.createLicense(api, userId, {
        license_type: type,
        license_number: number.trim(),
        state: state.trim() || undefined,
        expires_at: expires || undefined,
      }),
    onSuccess: () => {
      setNumber("");
      setState("");
      setExpires("");
      onAdded();
    },
  });

  const duplicate = existingTypes.has(type);
  const canSubmit =
    number.trim().length > 0 && !duplicate && !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-end",
        flexWrap: "wrap",
      }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          fontSize: 12,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as LicenseType)}
          style={{ ...field, width: 90 }}
        >
          {LICENSE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          fontSize: 12,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Number</span>
        <input
          value={number}
          placeholder="123456"
          onChange={(e) => setNumber(e.target.value)}
          style={{ ...field, width: 140 }}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          fontSize: 12,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>State</span>
        <input
          value={state}
          placeholder="TX"
          maxLength={2}
          onChange={(e) => setState(e.target.value)}
          style={{ ...field, width: 56 }}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          fontSize: 12,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Expires</span>
        <input
          type="date"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          style={{ ...field, width: 150 }}
        />
      </label>
      <button
        type="submit"
        className="rp-btn rp-btn--primary rp-btn--sm"
        disabled={!canSubmit}
        title={duplicate ? `A ${type} license already exists` : undefined}
      >
        {mutation.isPending ? "Adding…" : "Add license"}
      </button>
      {duplicate && (
        <span style={{ color: "var(--rp-color-text-muted)", fontSize: 12 }}>
          {type} already added
        </span>
      )}
      {mutation.isError && (
        <span style={{ color: "var(--rp-color-danger)", fontSize: 12 }}>
          {errMsg(mutation.error, "Could not add license")}
        </span>
      )}
    </form>
  );
}

function LicenseRow({
  license,
  onChanged,
}: {
  license: AgentLicense;
  onChanged: () => void;
}) {
  const verify = useMutation({
    mutationFn: () =>
      licenseService.updateLicense(api, license.user_id, license.id, {
        verified: license.verified_at === null,
      }),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () =>
      licenseService.deleteLicense(api, license.user_id, license.id),
    onSuccess: onChanged,
  });
  const verified = license.verified_at !== null;
  // "TX · expires 2027-04-30" — only the parts that exist, so a licence with
  // neither reads as one clean line instead of two em-dashes.
  const meta = [
    license.state,
    license.expires_at ? `expires ${license.expires_at}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        padding: "8px 10px",
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-md)",
        background: "var(--rp-color-surface)",
      }}
    >
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 999,
          background: "var(--rp-color-surface-2)",
          color: "var(--rp-color-text-muted)",
        }}
      >
        {license.license_type}
      </span>
      <span style={{ fontWeight: 600, fontSize: 13.5 }}>
        {license.license_number}
      </span>
      {meta && (
        <span style={{ color: "var(--rp-color-text-muted)", fontSize: 12.5 }}>
          {meta}
        </span>
      )}
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          padding: "2px 8px",
          borderRadius: 999,
          background: verified
            ? "var(--rp-color-success-faint)"
            : "var(--rp-color-surface-2)",
          color: verified
            ? "var(--rp-color-success)"
            : "var(--rp-color-text-muted)",
        }}
      >
        {verified ? "Verified" : "Unverified"}
      </span>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="rp-btn rp-btn--secondary rp-btn--sm"
        disabled={verify.isPending}
        onClick={() => verify.mutate()}
      >
        {verified ? "Unverify" : "Verify"}
      </button>
      <button
        type="button"
        className="rp-btn rp-btn--secondary rp-btn--sm"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
      >
        {remove.isPending ? "…" : "Remove"}
      </button>
    </div>
  );
}

export function AgentLicenses({ member }: { member: StaffMember }) {
  const queryClient = useQueryClient();
  const licenses = useQuery<AgentLicense[]>({
    queryKey: ["agent-licenses", member.id],
    queryFn: () => licenseService.listLicenses(api, member.id),
    staleTime: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["agent-licenses", member.id],
    });
    void queryClient.invalidateQueries({ queryKey: ["staff-members"] });
  };

  const existingTypes = new Set(
    (licenses.data ?? []).map((l) => l.license_type),
  );

  // No border/background of its own: the caller wraps this in a titled,
  // collapsible panel that supplies the chrome, and nesting a second box
  // inside it just drew a frame within a frame.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PhoneEditor member={member} onChanged={invalidate} />

      <div>
        {licenses.isLoading ? (
          <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
            Loading…
          </p>
        ) : licenses.isError ? (
          <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
            {errMsg(licenses.error, "Could not load licenses")}
          </p>
        ) : (licenses.data ?? []).length === 0 ? (
          <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
            No licenses on file.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(licenses.data ?? []).map((l) => (
              <LicenseRow key={l.id} license={l} onChanged={invalidate} />
            ))}
          </div>
        )}
      </div>

      <AddLicenseForm
        userId={member.id}
        existingTypes={existingTypes}
        onAdded={invalidate}
      />
    </div>
  );
}
